/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  AppBar,
  Tabs,
  Tab,
  Avatar,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import { useLocation } from "react-router";
import { useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { GET_USER_BY_ID } from "graphql/query/admin";
import Default from "Assets/Images/default.png";
import PortfolioTab from "./Components/PortfolioTab/PortfolioTab";
import OpportunityTab from "./Components/OpportunityTab/OpportunityTab";
import CoursesTab from "./Components/CoursesTab/CoursesTab";
import MarketplaceTab from "./Components/MarketplaceTab/MarketplaceTab";
import UserSubscriptionTab from "./Components/UserSubscriptionTab/UserSubscriptionTab";
import { useStyles } from "./ViewUserStyles";

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const a11yProps = (index) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

const InfoRow = ({ label, value }) => (
  <Grid container style={{ marginBottom: 10 }}>
    <Grid item xs={4} sm={3}>
      <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555" }}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={8} sm={9}>
      <Typography style={{ fontSize: 14 }}>{value || "N/A"}</Typography>
    </Grid>
  </Grid>
);

const ViewUser = () => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const { state } = useLocation();
  const { id = "", user: userFromState } = state || {};

  // When navigated with just an id (e.g. from Course Purchases), fetch the full
  // user via the detail API. When the list already passed the user object, reuse it.
  const { data, loading } = useQuery(GET_USER_BY_ID, {
    variables: { userId: id },
    skip: !id || Boolean(userFromState),
    fetchPolicy: "network-only",
  });

  const user = userFromState || data?.user;

  if (!id) {
    return (
      <Container maxWidth={false} disableGutters className={classes.app}>
        <Typography variant="subtitle1" className={classes.error || {}}>
          No user found.
        </Typography>
      </Container>
    );
  }

  if (!user && loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress size={28} />
      </Box>
    );
  }

  const socialLinks = user?.socialMediaLinks || {};
  const businessDetails = user?.businessDetails || {};
  const privacySetting = user?.privacySetting || {};
  const notificationSetting = user?.notificationSetting || {};
  const workLocations = user?.workLocations || [];

  return (
    <Container maxWidth={false} disableGutters className={classes.app}>
      {/* Breadcrumb */}
      <Typography className={classes.path}>
        <Link to="/admin/user" className={classes.link}>
          Users
        </Link>
        &nbsp;&gt;&nbsp;
        <Typography component="span" className={classes.cursorPointer}>
          View User
        </Typography>
      </Typography>

      {/* User Details Card */}
      <Box
        style={{
          background: "#fff",
          borderRadius: 8,
          marginTop: 16,
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {user?.coverPicture && (
          <img
            src={user.coverPicture}
            alt="Cover"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: 480,
              objectFit: "contain",
              display: "block",
              background: "#000",
            }}
          />
        )}
        <Box style={{ padding: "24px" }}>
        <Grid container spacing={3}>
          {/* Profile Picture */}
          <Grid item xs={12} sm={2} style={{ display: "flex", justifyContent: "center" }}>
            <Avatar
              src={user?.profilePicture || Default}
              alt={user?.fullName || "User"}
              style={{ width: 100, height: 100 }}
              imgProps={{ onError: (e) => { e.target.src = Default; } }}
            />
          </Grid>

          {/* Details */}
          <Grid item xs={12} sm={10}>
            <Grid container spacing={1} style={{ marginBottom: 8 }}>
              <Grid item>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  {user?.fullName || "N/A"}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={user?.isBlocked ? "Blocked" : "Active"}
                  size="small"
                  style={{
                    background: user?.isBlocked ? "#ffebee" : "#e8f5e9",
                    color: user?.isBlocked ? "#c62828" : "#2e7d32",
                    fontWeight: 600,
                    marginLeft: 8,
                    marginTop: 4,
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <InfoRow label="Role" value={Array.isArray(user?.role) ? user.role.join(", ") : user?.role} />
                <InfoRow label="Email" value={user?.email} />
                <InfoRow label="Mobile" value={user?.mobile} />
                <InfoRow label="Gender" value={user?.gender} />
                <InfoRow label="Category" value={user?.category} />
                <InfoRow label="Address" value={user?.address} />
                <InfoRow label="City / State" value={[user?.city, user?.state].filter(Boolean).join(", ")} />
                <InfoRow label="Sign Up Type" value={user?.signUpType} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoRow label="Specialization" value={user?.specialization} />
                <InfoRow
                  label="Experience"
                  value={user?.yearsOfExperience ? `${user.yearsOfExperience} years` : null}
                />
                <InfoRow
                  label="Profile Complete"
                  value={user?.isProfileCompleted ? "Yes" : "No"}
                />
                <InfoRow
                  label="Email Verified"
                  value={user?.isEmailVerified ? "Yes" : "No"}
                />
                <InfoRow label="Firebase UID" value={user?.firebaseUid} />
                <InfoRow
                  label="Last Activity"
                  value={user?.lastActivity ? new Date(user.lastActivity).toLocaleString() : null}
                />
                <InfoRow label="Subscribers" value={user?.subscriberCount} />
                <InfoRow label="App Theme" value={user?.appTheme} />
                <InfoRow label="Referral Code" value={user?.referralCode} />
                <InfoRow
                  label="Referral Reward Claimed"
                  value={user?.hasClaimedReferralReward ? "Yes" : "No"}
                />
              </Grid>
            </Grid>

            {/* Social Links */}
            {(socialLinks.instagram || socialLinks.youtube || socialLinks.website || socialLinks.other) && (
              <Box style={{ marginTop: 8 }}>
                <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
                  Social Links
                </Typography>
                <Grid container spacing={1}>
                  {socialLinks.instagram && (
                    <Grid item>
                      <Chip label={`Instagram: ${socialLinks.instagram}`} size="small" variant="outlined" />
                    </Grid>
                  )}
                  {socialLinks.youtube && (
                    <Grid item>
                      <Chip label={`YouTube: ${socialLinks.youtube}`} size="small" variant="outlined" />
                    </Grid>
                  )}
                  {socialLinks.website && (
                    <Grid item>
                      <Chip label={`Website: ${socialLinks.website}`} size="small" variant="outlined" />
                    </Grid>
                  )}
                  {socialLinks.other && (
                    <Grid item>
                      <Chip label={`Other: ${socialLinks.other}`} size="small" variant="outlined" />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Business Details */}
            {(businessDetails.businessName || businessDetails.gstNumber || businessDetails.businessLogo) && (
              <Box style={{ marginTop: 16 }}>
                <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
                  Business Details
                </Typography>
                <InfoRow label="Business Name" value={businessDetails.businessName} />
                <InfoRow label="GST Number" value={businessDetails.gstNumber} />
              </Box>
            )}

            {/* Privacy Settings */}
            {Object.keys(privacySetting).length > 0 && (
              <Box style={{ marginTop: 16 }}>
                <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
                  Privacy Settings
                </Typography>
                <Grid container spacing={1}>
                  <Grid item>
                    <Chip label={`Portfolio: ${privacySetting.portfolioVisibility || "N/A"}`} size="small" variant="outlined" />
                  </Grid>
                  <Grid item>
                    <Chip label={`Show Email: ${privacySetting.showEmail ? "Yes" : "No"}`} size="small" variant="outlined" />
                  </Grid>
                  <Grid item>
                    <Chip label={`Show Phone: ${privacySetting.showPhone ? "Yes" : "No"}`} size="small" variant="outlined" />
                  </Grid>
                  <Grid item>
                    <Chip label={`Show Location: ${privacySetting.showLocation ? "Yes" : "No"}`} size="small" variant="outlined" />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Notification Settings */}
            {Object.keys(notificationSetting).length > 0 && (
              <Box style={{ marginTop: 16 }}>
                <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
                  Notification Settings
                </Typography>
                <Grid container spacing={1}>
                  {Object.entries(notificationSetting).map(([key, value]) => (
                    <Grid item key={key}>
                      <Chip
                        label={`${key}: ${value ? "On" : "Off"}`}
                        size="small"
                        variant="outlined"
                        style={{ textTransform: "capitalize" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Work Locations */}
            {workLocations.length > 0 && (
              <Box style={{ marginTop: 16 }}>
                <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
                  Work Locations
                </Typography>
                <Grid container spacing={1}>
                  {workLocations.map((loc, i) => (
                    <Grid item key={loc.place || i}>
                      <Chip label={loc.place || "N/A"} size="small" variant="outlined" />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Grid>
        </Grid>
        </Box>
      </Box>

      {/* Tabs */}
      <Box style={{ marginTop: 24 }}>
        <AppBar position="static" style={{ boxShadow: "none", backgroundColor: "#fff" }}>
          <Tabs
            TabIndicatorProps={{ style: { background: "var(--color-primary, #000)", height: 2 } }}
            value={tabValue}
            onChange={(e, newVal) => setTabValue(newVal)}
            style={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#fff", color: "#000" }}
          >
            <Tab label="Portfolio" {...a11yProps(0)} style={{ fontWeight: 550, fontSize: 16, textTransform: "none", minWidth: 140 }} />
            <Tab label="Opportunity" {...a11yProps(1)} style={{ fontWeight: 550, fontSize: 16, textTransform: "none", minWidth: 140 }} />
            <Tab label="Courses" {...a11yProps(2)} style={{ fontWeight: 550, fontSize: 16, textTransform: "none", minWidth: 140 }} />
            <Tab label="Marketplace" {...a11yProps(3)} style={{ fontWeight: 550, fontSize: 16, textTransform: "none", minWidth: 140 }} />
            <Tab label="Subscriptions" {...a11yProps(4)} style={{ fontWeight: 550, fontSize: 16, textTransform: "none", minWidth: 140 }} />
          </Tabs>
        </AppBar>

        <TabPanel value={tabValue} index={0}>
          <PortfolioTab userId={id} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <OpportunityTab userId={id} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <CoursesTab userId={id} />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <MarketplaceTab userId={id} />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <UserSubscriptionTab userId={id} />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default ViewUser;
